/**
 * Field validators — the "what makes this value valid" judgments.
 *
 * These live here, NOT in the input atoms. PSInput / PSMobileInput are
 * render-only: the screen decides a value is invalid and passes down an `error`
 * string (see the PSInput docstring). This module is just where that judgment
 * is written once so two screens can't disagree about it.
 */

// Practical length caps from the mail specs: an address is at most 254 chars on
// the wire (RFC 5321), its local part at most 64, its domain at most 255. A
// value past any of these will be rejected by the mail server anyway, so we may
// as well say so before sending an OTP to it.
const MAX_ADDRESS_LENGTH = 254;
const MAX_LOCAL_LENGTH = 64;
const MAX_DOMAIN_LENGTH = 255;
const MAX_LABEL_LENGTH = 63;

// The part before the '@', in RFC 5322's unquoted "dot-atom" form: runs of these
// characters joined by SINGLE dots. Anchored, so a leading dot, a trailing dot,
// and a `..` run all fail rather than matching a substring.
//
// We deliberately don't accept the quoted form (`"john doe"@example.com`).
// It's legal, effectively never used by real people, and accepting it means
// accepting spaces inside the address — which turns every fat-fingered
// "john doe@gmail.com" into a valid-looking value we'd send an OTP to.
//
// ASCII only, for the same reason: internationalised (IDN / SMTPUTF8) addresses
// are real but rare here, and half the mail path still chokes on them. If we
// ever need them, this is the one line to revisit.
const LOCAL_PART =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;

// One dot-separated piece of the domain: alphanumeric ends with optional inner
// hyphens (`my-host` yes, `-host` / `host-` no). Length is checked separately so
// the message stays in one place.
const DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

// The last label. Letters only and at least two of them: rules out `foo@bar.c`,
// `foo@bar.123`, and anything that ends mid-typing on a dot.
const TOP_LEVEL_DOMAIN = /^[A-Za-z]{2,}$/;

/**
 * Is this a plausibly deliverable email address?
 *
 * Structural only — it can't tell you the mailbox exists (that's what the
 * emailed OTP is for). The bar is "a real address a real person could receive
 * mail at", which is stricter than RFC-legal in two intentional ways: no quoted
 * local parts, and a registrable domain is required (`foo@localhost` is a valid
 * address on a mail host, but not one we can reach a user at).
 *
 * Surrounding whitespace is trimmed before testing — soft keyboards and paste
 * both love a trailing space, and that's a typo we can absorb rather than
 * scold. Whitespace anywhere INSIDE the address still fails. Callers that store
 * the value should store it trimmed too, so what they keep is what we approved.
 */
export function isValidEmail(value: string): boolean {
  const address = value.trim();
  if (address.length === 0 || address.length > MAX_ADDRESS_LENGTH) return false;

  // Split on '@' instead of regexing around it, so `a@b@c.com` fails outright
  // rather than quietly matching the last pair.
  const parts = address.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;

  if (local.length > MAX_LOCAL_LENGTH || !LOCAL_PART.test(local)) return false;
  if (domain.length > MAX_DOMAIN_LENGTH) return false;

  const labels = domain.split('.');
  // At least two labels — see the registrable-domain note above. An empty label
  // (from `a@.com`, `a@b..com`, `a@b.com.`) fails DOMAIN_LABEL below.
  if (labels.length < 2) return false;
  if (
    !labels.every((l) => l.length <= MAX_LABEL_LENGTH && DOMAIN_LABEL.test(l))
  )
    return false;

  return TOP_LEVEL_DOMAIN.test(labels[labels.length - 1]);
}

// The three platforms we ask for. Both of Twitter's hosts are the same platform,
// so a link is judged by where it POINTS, not by which name the user pasted.
const SOCIAL_HOSTS = ['instagram.com', 'twitter.com', 'x.com', 'linkedin.com'];

// A profile URL is short. The cap is a practical bound (Django's URLField
// defaults to 200) so an over-long paste is refused here rather than by a 400.
const MAX_LINK_LENGTH = 200;

/**
 * Is this an Instagram / X / LinkedIn profile link — and if so, what do we send?
 *
 * Returns the canonical `https://…` URL, or null when it isn't one of the three.
 * ONE function for both jobs on purpose: `PATCH /me` takes `social_media_link`
 * at face value ("must be a URL"), so the platform judgment is entirely ours,
 * and a caller that validated one string and sent another is the bug this shape
 * makes unwritable.
 *
 * Two things it does beyond matching a host:
 *
 *   - Adds the scheme when the user didn't. People paste `instagram.com/them`,
 *     and the server's URL check rejects that — a fix we can make silently
 *     rather than scold them for.
 *   - Reads the host as a URL parser would, not as a substring. The authority is
 *     taken up to the first `/?#`, anything before an `@` (userinfo) is dropped,
 *     and the match is anchored on a dot boundary — so
 *     `https://instagram.com@evil.com/x` resolves to `evil.com` and is refused,
 *     while `in.linkedin.com/in/them` passes.
 *
 * A bare homepage (`instagram.com`, no handle) is refused too: it identifies
 * nobody, and it's what a half-finished paste looks like.
 */
export function normalizeSocialLink(value: string): string | null {
  const link = value.trim();
  if (link.length === 0 || link.length > MAX_LINK_LENGTH) return null;
  // Whitespace inside means they pasted a sentence, not a link.
  if (/\s/.test(link)) return null;

  const withScheme = /^https?:\/\//i.test(link) ? link : `https://${link}`;
  const afterScheme = withScheme.replace(/^https?:\/\//i, '');

  const split = afterScheme.search(/[/?#]/);
  const authority = split === -1 ? afterScheme : afterScheme.slice(0, split);
  const path = split === -1 ? '' : afterScheme.slice(split);

  // `pop()` on a split can't be undefined, but it's typed as if it can.
  const host = (authority.split('@').pop() ?? '').toLowerCase();
  // A port on a profile link means it isn't one.
  if (host.includes(':')) return null;
  if (!SOCIAL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)))
    return null;

  const handle = path.replace(/^\//, '').split(/[?#]/)[0];
  if (handle.length === 0) return null;

  return `https://${host}${path}`;
}
